//lib
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
//src
import useCompany from '../../hooks/useCompany.js';
import {
  calculateTotalBudget,
  calculateTotalHours,
  hasAnyMaterialValid,
  saveSingleProject,
  validateMaterials,
  validateMilestonesAndTasks,
  validateUserPermissions,
  getProjectDatesFromMilestones,
} from './planner.utils.js';
import { calculateBudgetForTask } from './milestone/utils/budget.utils.js';
import GanttChart from '../planner/gantt-chart/GanttChart';
import {
  ALERT_MESSAGES,
  DEFAULT_MATERIALS,
  DEFAULT_MILESTONE,
  DEFAULT_PLANNER_STATE,
  GENERAL,
} from './planner.constants.js';
import { getDiscountedValue } from './totals/planner-totals.utils';
import {
  dateAdustmentForTemplateSelectionLogic,
  deleteTaskLogic,
  milestoneUpdateLogic,
  navigateToBudgetLogic,
  taskUpdateLogic,
  updateMaterialLogic,
} from './planner-handlers.js';
import { Button } from '../../components/button/button';
import useProjectData, { usePlannerValues, useProjectDetails } from './planner.hooks.js';
import Material from './material/Material';
import PlannerHeader from './plannerHeader.js';
import MilestoneList from './milestone/milestone.js';
import { isSomething } from '../../utils/utils.js';
import { PlannerStyled } from './planner.style.js';
import PlannerTotals from './totals/planner-totals.js';
import { useAuth } from '../../context/AuthContext.js';
import Loader from '../../components/loader/loader.js';
import NoDataState from './no-data-state/no-data.state';
import ScopeField from './components/scope-field/scope-field.js';
import Drawer from './drawer/drawer.js';

const Planner = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const newMilestoneRef = useRef(null);
  const projectLoadRef = useRef(null);
  const { userPermissions } = useAuth();
  const [isGanttOpen, setIsGanttOpen] = useState(false);

  const [plannerState, setPlannerState] = useState({
    ...DEFAULT_PLANNER_STATE,
    milestones: DEFAULT_MILESTONE, // Always an array
  });

  const {
    projectData,
    milestones,
    materials,
    resources,
    templateBuilderData,
    byPassTemplateSelection,
    serviceDiscount,
    serviceDiscountUnit,
    materialDiscount,
    materialDiscountUnit,
  } = plannerState;
  const { data: company, isLoading: isCompanyLoading } = useCompany(projectData?.client);

  const { project, isProjectLoading, loggedInstances, isLoggedInstancesLoading, templatesList } =
    useProjectData(id);

  const { projectDetails, materialTotal, noMilestoneProject } = usePlannerValues({
    project,
    milestones,
    materials,
    project,
  });

  const plannerServiceValue = useMemo(() => {
    const { plannerType, plannerServiceValue: manualValue } = plannerState.projectData;

    // In Fixed-Fee Project we want the user’s input, not the sum of milestone budgets
    if (plannerType === 'Fixed Fee Project') {
      // they type it into projectData.plannerServiceValue via your <TextInputField>
      return Number(manualValue) || 0;
    }

    // otherwise fall back to normal calculation
    return calculateTotalBudget(plannerState.milestones || []);
  }, [
    plannerState.milestones,
    plannerState.projectData.plannerType,
    plannerState.projectData.plannerServiceValue,
  ]);

  const plannerServiceHours = useMemo(() => {
    return calculateTotalHours(plannerState.milestones || []);
  }, [plannerState.milestones]);

  const projectTotal = useMemo(() => {
    const materialVal = materials?.reduce((sum, mat) => sum + parseFloat(mat.total || 0), 0);
    return plannerServiceValue + materialVal;
  }, [plannerServiceValue, materials]);

  const discount = {
    serviceDiscount,
    materialDiscount,
    serviceDiscountUnit,
    materialDiscountUnit,
  };

  const plannerType = plannerState.projectData.plannerType;

  const isTimelinesInValid =
    // 1. non‐Materials without any deliverables ⇒ invalid
    (noMilestoneProject && plannerType !== 'Materials') ||
    // 2. Materials type but no materials ⇒ invalid
    (plannerType === 'Materials' && !hasAnyMaterialValid(materials));

  const isProjectDirty = projectDetails?.plannerType;
  const showNoDataState = !isProjectDirty && !byPassTemplateSelection;

  const handleMilestoneChange = (index, field, value) => {
    const updatedMilestones = milestoneUpdateLogic({
      index,
      field,
      value,
      milestones,
    });

    setPlannerState((prev) => ({ ...prev, milestones: updatedMilestones }));
  };

  const handleTaskChange = (milestoneIndex, taskIndex, field, value) => {
    const updatedMilestones = taskUpdateLogic({
      milestoneIndex,
      taskIndex,
      field,
      value,
      milestones,
    });

    setPlannerState((prev) => ({ ...prev, milestones: updatedMilestones }));
  };

  const handleDeleteTask = (milestoneIndex, taskIndex) => {
    const updatedMilestones = deleteTaskLogic({
      milestoneIndex,
      taskIndex,
      milestones,
    });
    setPlannerState((prev) => ({ ...prev, milestones: updatedMilestones }));
  };

  const handleDeleteMilestone = (milestoneIndex) => {
    if (milestones.length === 1) {
      alert(ALERT_MESSAGES.Deliverable);
      return;
    }

    if (window.confirm(ALERT_MESSAGES.Delete)) {
      // Get the milestone that should be deleted
      const milestoneToDelete = milestones[milestoneIndex];
      console.log('Deleting milestone with id:', milestoneToDelete.id);

      // Filter out the milestone from your milestones array
      const newMilestones = milestones.filter((_, idx) => idx !== milestoneIndex);

      // Also filter out the corresponding deliverable from builderData.deliverables
      const newDeliverables = project.deliverables.filter((del) => del.id !== milestoneToDelete.id);

      console.log('New deliverables:', newDeliverables);
      // Update your project object
      const newproject = {
        ...project,
        deliverables: newDeliverables,
      };

      console.log('newproject', newproject);

      // Update state so that both milestones and project are updated
      setPlannerState((prev) => ({
        ...prev,
        milestones: newMilestones,
        project: newproject,
      }));
    }
  };

  const handleMaterialTaskChange = (materialIndex, taskIndex, field, value) => {
    const updatedMaterials = updateMaterialLogic({
      materialIndex,
      taskIndex,
      field,
      value,
      materials,
    });

    setPlannerState((prev) => ({ ...prev, materials: updatedMaterials }));
  };

  const handleMaterialDeleteTask = (materialIndex) => {
    // If there's only one material in the array, do not allow deletion.
    if (materials.length === 1) {
      alert(ALERT_MESSAGES.DeleteMaterialDescription);
      return;
    }
    const updatedMaterials = [...materials];
    updatedMaterials.splice(materialIndex, 1);
    setPlannerState((prev) => ({ ...prev, materials: updatedMaterials }));
  };

  const navigateToBudget = (milestoneName) => {
    const milestone = milestones?.find((m) => m.name === milestoneName);
    navigateToBudgetLogic({ milestone, navigate, id });
  };

  const handleTemplateSelection = (template, startDate) => {
    const newStartDate = startDate ? new Date(startDate) : new Date();

    const {
      milestones: adjustedMilestones,
      plannerStartDate: adjustedStart,
      plannerEndDate: adjustedEnd,
    } = dateAdustmentForTemplateSelectionLogic(
      template.deliverables,
      newStartDate,
      template.data.plannerType,
      template.data.plannerStartDate,
      template.data.plannerEndDate,
    );

    setPlannerState((prev) => ({
      ...prev,
      projectData: {
        ...prev?.projectData,
        projectScope: template.data.projectScope,
        plannerServiceValue: template.data.plannerServiceValue,
        plannerEndDate: template.data.plannerEndDate,
        plannerStartDate: template.data.plannerStartDate,
        plannerType: template.data.plannerType,
        plannerStartDate: adjustedStart,
        plannerEndDate: adjustedEnd,
      },
      milestones: adjustedMilestones,
      materials: template.materials || [],
      resources: template.resources || [],
      byPassTemplateSelection: true,
      templateBuilderData: template?.builderData,
    }));
    return;
  };

  const handleAddMaterialTask = () => {
    setPlannerState((prevState) => ({
      ...prevState,
      materials: [
        ...prevState.materials,
        {
          cost: '',
          description: '',
          expenseType: '',
          markup: '',
          orderNumber: 'N/A',
          override: '',
          quantity: '',
          total: '',
          incurredDate: '',
        },
      ],
    }));
  };
  const handleAddMilestone = () => {
    setPlannerState((prev) => ({
      ...prev,
      milestones: [
        ...prev.milestones,
        {
          name: '',
          dateRange: '',
          id: uuid(),
          tasks: [
            {
              description: '',
              id: uuid(),
              organization: 'FKA',
              hours: 0,
              budget: 0,
              startDate: null,
              endDate: null,
              resources: [
                {
                  hours: 0,
                  id: uuid(),
                  isEnabled: false,
                },
              ],
            },
          ],
          hours: 0,
          budget: 0,
        },
      ],
    }));
  };

  const handleStatusChange = (newStatus) => {
    setPlannerState((prev) => ({
      ...prev,
      projectData: { ...prev?.projectData, status: newStatus },
    }));
    handleSave(newStatus, false, true);
  };

  const onPlannerTypeChange = (type) => {
    setPlannerState((prev) => ({
      ...prev,
      projectData: { ...prev?.projectData, plannerType: type },
    }));
    handleSave(type, false, true);
  };

  const discountedServiceTotal = getDiscountedValue(
    plannerServiceValue,
    serviceDiscount,
    serviceDiscountUnit,
  );

  const discountedMaterialTotal = getDiscountedValue(
    projectTotal - plannerServiceValue,
    materialDiscount,
    materialDiscountUnit,
  );

  const finalProjectTotal = discountedServiceTotal + discountedMaterialTotal;

  const handleSave = async (
    statusToUpdate = '',
    showCongratsAlert = true,
    skipValidation = false,
  ) => {
    if (!skipValidation) {
      if (!validateUserPermissions(userPermissions, isTimelinesInValid, projectData.plannerType))
        return;
      if (
        !validateMilestonesAndTasks(
          milestones,
          noMilestoneProject,
          plannerState.projectData.plannerType,
        )
      )
        return;
      if (!validateMaterials(materials, noMilestoneProject)) return;
    }

    // Step 1: Format all deliverables (milestones)

    const fixedFeeTypes = ['Fixed Fee Deliverable', 'Fixed Fee Project'];
    const isFixedFeeProject = projectData.plannerType === 'Fixed Fee Project';

    const formattedDeliverables = milestones.map((milestone) => {
      // For fixed-fee types, preserve manual budget & dateRange
      if (fixedFeeTypes.includes(plannerState.projectData.plannerType)) {
        return {
          ...milestone,
          // tasks not relevant for fixed-fee
          tasks: [],
          // hours fixed to zero
          hours: 0,
          // use user-entered milestone.budget
          budget: parseFloat(milestone.budget) || 0,
        };
      }
      const existingMilestone = plannerState.milestones.find((m) => m.id === milestone.id) || {};

      const updatedTasks = milestone.tasks.map((task) => {
        const existingTask = existingMilestone.tasks?.find((t) => t.id === task.id) || {};

        return {
          ...task,
          hours: existingTask.hours ?? task.hours,
          budget: existingTask.budget ?? task.budget,
          startDate: task.startDate || null,
          endDate: task.endDate || null,
          resources:
            task.resources?.map((res) => ({
              id: res.id,
              hours: res.hours ?? 0,
              isEnabled: res.isEnabled ?? false,
            })) ?? [],
        };
      });

      const totalTaskHours = updatedTasks.reduce((sum, t) => sum + (t.hours || 0), 0);
      const totalTaskBudget = updatedTasks.reduce((sum, t) => sum + (t.budget || 0), 0);

      return {
        ...milestone,
        tasks: updatedTasks,
        hours: totalTaskHours,
        budget: totalTaskBudget,
      };
    });
    // Step 2: Determine planner dates

    let plannerStartDate, plannerEndDate;
    if (isFixedFeeProject) {
      plannerStartDate = projectData.plannerStartDate;
      plannerEndDate = projectData.plannerEndDate;
    } else {
      ({ plannerStartDate, plannerEndDate } = getProjectDatesFromMilestones(
        plannerState.milestones,
      ));
    }

    // Step 3: Determine service value
    const serviceValueToSave = isFixedFeeProject
      ? Number(projectData.plannerServiceValue)
      : Number(plannerServiceValue);

    // Step 4: Calculate totals
    const matTotal = materials.reduce((sum, m) => sum + parseFloat(m.total || 0), 0);
    const projectTotal = serviceValueToSave + matTotal;

    // Step 5: Prepare updated state
    const updatedPlannerState = {
      ...plannerState,
      milestones: formattedDeliverables,
      projectData: {
        ...projectData,
        plannerStartDate,
        plannerEndDate,
        plannerServiceValue: serviceValueToSave,
        plannerServiceHours,
        projectTotal: projectTotal,
      },
    };

    // Step 6: Persist
    if (id) {
      await saveSingleProject({
        id,
        plannerState: updatedPlannerState,
        plannerServiceValue,
        plannerServiceHours,
        statusToUpdate,
        templateBuilderData,
        showCongratsAlert,
      });
    } else {
      console.error('Error: Project ID is not defined.');
    }

    // Optional reload if using templates
    if (isSomething(templateBuilderData)) window.location.reload();

    // Step 5: Update local state after save
    setPlannerState((prev) => ({
      ...prev,
      milestones: formattedDeliverables,
      projectData: {
        ...prev.projectData,
        plannerStartDate,
        plannerEndDate,
        plannerServiceValue,
        projectTotal,
      },
    }));
  };

  useProjectDetails({
    projectDetails,
    setPlannerState,
    projectLoadRef,
  });

  useEffect(() => {
    const { plannerType } = projectData;
    if (plannerType !== 'Fixed Fee Project') {
      const { plannerStartDate, plannerEndDate } = getProjectDatesFromMilestones(milestones);
      setPlannerState((prev) => ({
        ...prev,
        projectData: {
          ...prev.projectData,
          plannerStartDate,
          plannerEndDate,
        },
      }));
    }
  }, [projectData.plannerType, milestones]);

  const updateDeliverables = useCallback((newDeliverables) => {
    setPlannerState((prev) => ({
      ...prev,
      milestones: newDeliverables,
    }));
  }, []);

  const [showDeleteModal, setShowDeleteModal] = useState(null); // You already use this

  const handleDeliverableUpdate = (
    deliverableId,
    taskId,
    resourceId,
    updatedResource,
    actionType, // "enable" or "disable"
  ) => {
    // Step 1: If disabling, check for logged instances
    if (actionType === 'disable') {
      const role = resources.find((r) => r.id === resourceId)?.role;

      const isLogged = loggedInstances.some((log) => {
        const isMatchingDeliverable = log?.deliverableId === deliverableId;

        const isMatchingRole = log?.role === role;
        const hasLoggedHours = log?.hours?.some((entry) => Number(entry?.hours) > 0);

        return isMatchingDeliverable && isMatchingRole && hasLoggedHours;
      });

      if (isLogged) {
        alert('You cannot disable a role with time logged against it.');
        return;
      }
    }
    // Step 2: Update milestones
    setPlannerState((prev) => {
      const updatedMilestones = prev.milestones.map((milestone) => {
        if (milestone.id !== deliverableId) {
          return milestone;
        }
        const updatedTasks = milestone.tasks.map((task) => {
          if (task.id !== taskId) {
            return task;
          }
          const currentResources = task.resources || [];
          const resourceExists = currentResources.some((r) => r.id === resourceId);

          const updatedResources = resourceExists
            ? currentResources.map((r) => (r.id === resourceId ? updatedResource : r))
            : [...currentResources, updatedResource];

          // Step 2b: Recalculate task-level hours and budget
          const taskHours = updatedResources.reduce((sum, r) => sum + Number(r.hours || 0), 0);

          const taskBudget = updatedResources.reduce((sum, r) => {
            const rate = resources.find((res) => res.id === r.id)?.rate || 0;
            return sum + (r?.isEnabled ? rate * Number(r.hours || 0) : 0);
          }, 0);

          return {
            ...task,
            resources: updatedResources,
            hours: taskHours,
            budget: taskBudget,
          };
        });

        // Step 3: Recalculate milestone-level hours and budget
        const milestoneHours = updatedTasks.reduce((sum, t) => sum + Number(t.hours || 0), 0);

        const milestoneBudget = updatedTasks.reduce((sum, t) => sum + Number(t.budget || 0), 0);

        return {
          ...milestone,
          tasks: updatedTasks,
          hours: milestoneHours,
          budget: milestoneBudget,
        };
      });

      return {
        ...prev,
        milestones: updatedMilestones,
      };
    });
  };
  const setResourcesDirectly = (update) => {
    setPlannerState((prev) => ({
      ...prev,
      resources: typeof update === 'function' ? update(prev.resources) : update,
    }));
  };

  // inside Planner component
  const handleRateOptionChange = (value) => {
    setPlannerState((prev) => ({
      ...prev,
      projectData: {
        ...prev.projectData,
        rateOption: value,
      },
    }));
  };

  useEffect(() => {
    if (
      ['Fixed Fee Deliverable', 'Fixed Fee Project'].includes(plannerState.projectData.plannerType)
    ) {
      return;
    }
    // For each milestone, recalculate each task’s budget and hours using the updated resources
    const updatedMilestones = plannerState.milestones.map((milestone) => {
      const updatedTasks = milestone.tasks.map((task) => {
        const { hours, value } = calculateBudgetForTask(task, plannerState.resources);
        return {
          ...task,
          hours, // Updated task hours
          budget: value, // Updated task budget
        };
      });
      // Sum up the milestone-level hours and budget
      const milestoneHours = updatedTasks.reduce((sum, t) => sum + Number(t.hours || 0), 0);
      const milestoneBudget = updatedTasks.reduce((sum, t) => sum + Number(t.budget || 0), 0);
      return {
        ...milestone,
        tasks: updatedTasks,
        hours: milestoneHours,
        budget: milestoneBudget,
      };
    });

    // Update plannerState with recalculated milestones
    setPlannerState((prev) => ({
      ...prev,
      milestones: updatedMilestones,
    }));
  }, [plannerState.resources]); // Run when resources change

  if (isLoggedInstancesLoading || isProjectLoading || isCompanyLoading) return <Loader absolute />;

  return (
    <PlannerStyled plannerType={projectData.plannerType}>
      <PlannerHeader
        id={id}
        discount={0}
        onToggleGantt={() => setIsGanttOpen(true)}
        materials={materials}
        milestones={milestones}
        resources={resources}
        handleSave={handleSave}
        discountObj={discount}
        projectData={plannerState.projectData}
        contact={project.contact}
        project={plannerState}
        company={company}
        handleAddMilestone={handleAddMilestone}
        handleStatusChange={(newStatus) => handleStatusChange(newStatus)}
        onPlannerTypeChange={(type) => onPlannerTypeChange(type)}
        projectTotal={finalProjectTotal}
        isGeneralUser={userPermissions === GENERAL}
      />

      {!isProjectLoading && showNoDataState && (
        <div>
          <NoDataState
            templatesList={templatesList}
            onStartFromScratch={(type) => {
              setPlannerState((prev) => ({
                ...prev,
                milestones: DEFAULT_MILESTONE,
                materials: DEFAULT_MATERIALS,
                byPassTemplateSelection: true,
                projectData: {
                  ...prev.projectData,
                  plannerType: type,
                },
              }));
            }}
            onTemplateSelection={handleTemplateSelection}
            onPlannerTypeChange={(type) => {
              setPlannerState((prev) => ({
                ...prev,
                projectData: {
                  ...prev.projectData,
                  plannerType: type,
                },
              }));
            }}
          />
        </div>
      )}
      {!isProjectLoading && !showNoDataState && (
        <div className="planner-container">
          {projectData.plannerType !== 'Materials' && (
            <Drawer
              project={project}
              resources={resources}
              setResources={setResourcesDirectly}
              deliverables={milestones}
              setDeliverables={updateDeliverables}
              company={company}
              loggedRolesList={loggedInstances?.map((x) => x?.role) || []}
              rateOption={plannerState.projectData.rateOption}
              onRateOptionChange={handleRateOptionChange}
            />
          )}

          <div className="milestone-box">
            <ScopeField
              scope={projectData.projectScope}
              isGeneralUser={userPermissions === GENERAL}
              handleChange={(value) => {
                setPlannerState((prev) => ({
                  ...prev,
                  projectData: {
                    ...prev?.projectData,
                    projectScope: value,
                  },
                }));
              }}
            />
            {projectData.plannerType !== 'Materials' && (
              <MilestoneList
                serviceTotal={plannerServiceValue}
                milestones={Array.isArray(milestones) ? milestones : []}
                setPlannerState={setPlannerState}
                resources={resources}
                newMilestoneRef={newMilestoneRef}
                handleMilestoneChange={handleMilestoneChange}
                handleDeleteMilestone={handleDeleteMilestone}
                navigateToBudget={navigateToBudget}
                handleTaskChange={handleTaskChange}
                handleAddMilestone={handleAddMilestone}
                handleDeleteTask={handleDeleteTask}
                handleDeliverableUpdate={handleDeliverableUpdate}
                project={plannerState}
                loggedInstances={loggedInstances}
                isGeneralUser={userPermissions === GENERAL}
                handleStatusChange={handleStatusChange}
              />
            )}

            <div className="materials-section">
              <Material
                materials={materials}
                handleMaterialTaskChange={handleMaterialTaskChange}
                handleAddMaterialTask={handleAddMaterialTask}
                handleMaterialDeleteTask={handleMaterialDeleteTask}
                isGeneralUser={userPermissions === GENERAL}
              />
            </div>

            <PlannerTotals
              projectData={projectData}
              calculatedProjectTotal={projectTotal}
              plannerServiceValue={plannerServiceValue}
              plannerState={plannerState}
              isGeneralUser={userPermissions === GENERAL}
              setPlannerState={setPlannerState}
              handleSave={handleSave}
            />
          </div>
        </div>
      )}
      <Dialog
        open={isGanttOpen}
        keepMounted
        onClose={() => setIsGanttOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent dividers>
          <GanttChart />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsGanttOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </PlannerStyled>
  );
};

export default Planner;
